import {
  type InfiniteData,
  infiniteQueryOptions,
  mutationOptions,
} from "@tanstack/react-query";
import {
  CHANGE_GALLERY_ITEM_FAVORITE_STATUS,
  GET_GALLERY_ITEMS,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import type {
  GalleryItem,
  GalleryItemsResponse,
  GalleryKind,
  ToggleFavoriteVariables,
} from "./types";

export const GALLERY_PAGE_SIZE = 20;

export type GalleryPage = {
  items: GalleryItem[];
  nextSkip: number;
};

// BE は favorite / non_favorite を別配列で返すが、ここで 1 本のフラット配列に統合する。
// 2 配列のまま保持するといいねトグル時にアイテムが配列間を移動して順番がジャンプするため、
// 単一配列にしてフラグだけ書き換えられるようにしている。
const normalize = (
  data: GalleryItemsResponse | undefined,
  skip: number,
): GalleryPage => {
  if (!data) return { items: [], nextSkip: skip };
  const favorites = (data.favorite_gallery_list ?? []).map((entry) => ({
    userId: entry.gallery_user.user_id,
    userName: entry.gallery_user.user_name,
    avatarId: entry.gallery_user.ava_id,
    age: entry.gallery_user.age,
    fileId: entry.opened_content.file_id,
    isFavorite: true,
  }));
  const others = (data.non_favorite_gallery_list ?? []).map((entry) => ({
    userId: entry.gallery_user.user_id,
    userName: entry.gallery_user.user_name,
    avatarId: entry.gallery_user.ava_id,
    age: entry.gallery_user.age,
    fileId: entry.opened_content.file_id,
    isFavorite: false,
  }));
  const items = [...favorites, ...others];
  return { items, nextSkip: skip + items.length };
};

export const galleryItemsInfiniteQueryOptions = (kind: GalleryKind) => {
  const isImage = kind === "image";
  return infiniteQueryOptions({
    queryKey: queryKeys.gallery.items(kind),
    queryFn: async ({ pageParam }) => {
      const raw = await apiClient<GalleryItemsResponse>(GET_GALLERY_ITEMS, {
        is_image: isImage,
        take: GALLERY_PAGE_SIZE,
        skip: pageParam,
      });
      return normalize(raw, pageParam);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.items.length < GALLERY_PAGE_SIZE ? undefined : lastPage.nextSkip,
  });
};

type GalleryInfiniteData = InfiniteData<GalleryPage, number>;

const mapPages = (
  data: GalleryInfiniteData,
  fileId: string,
  nextIsFavorite: boolean,
): GalleryInfiniteData => {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) =>
        item.fileId === fileId ? { ...item, isFavorite: nextIsFavorite } : item,
      ),
    })),
  };
};

export const toggleGalleryFavoriteMutationOptions = mutationOptions({
  mutationFn: (vars: ToggleFavoriteVariables) =>
    apiClient<unknown>(CHANGE_GALLERY_ITEM_FAVORITE_STATUS, {
      file_id: vars.fileId,
      is_favorite: vars.nextIsFavorite,
    }),
  onMutate: async (vars) => {
    const queryKey = queryKeys.gallery.items(vars.kind);
    await queryClient.cancelQueries({ queryKey });
    const previous = queryClient.getQueryData<GalleryInfiniteData>(queryKey);
    if (!previous) return { previous };

    queryClient.setQueryData<GalleryInfiniteData>(
      queryKey,
      mapPages(previous, vars.fileId, vars.nextIsFavorite),
    );

    return { previous };
  },
  onError: (_err, vars, context) => {
    if (context?.previous) {
      queryClient.setQueryData(
        queryKeys.gallery.items(vars.kind),
        context.previous,
      );
    }
  },
});
